'use client';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';
import { categories, effects, projects, ui, type Category } from '../data';
import { useLang } from './Lang';
import { Arrow } from './Icons';
import { Fade, Words } from './Reveal';

const ShaderImage = dynamic(() => import('./ShaderImage'), { ssr: false });

export default function ProjectIndex() {
    const { lang, t } = useLang();
    const reduce = useReducedMotion();
    const [filter, setFilter] = useState<Category | 'all'>('all');
    const [active, setActive] = useState<string | null>(null);
    const [preview, setPreview] = useState(projects[0].slug); // garde le canvas monté entre deux survols
    const x = useSpring(useMotionValue(0), { stiffness: 180, damping: 22 });
    const y = useSpring(useMotionValue(0), { stiffness: 180, damping: 22 });

    useEffect(() => {
        const move = (e: PointerEvent) => {
            x.set(e.clientX);
            y.set(e.clientY);
        };
        window.addEventListener('pointermove', move);
        return () => window.removeEventListener('pointermove', move);
    }, [x, y]);

    const shown = projects.filter((p) => filter === 'all' || p.category === filter);
    const current = projects.find((p) => p.slug === preview)!;
    const used = [...new Set(projects.map((p) => p.category))];

    return (
        <section id="work" className="px-4 py-24 md:px-10 md:py-36">
            <div className="mb-12 flex flex-wrap items-end justify-between gap-6 border-b border-(--pf-ink)/20 pb-6">
                <Words text={t(ui.work)} className="font-(family-name:--pf-serif) text-5xl md:text-7xl" />
                <div className="flex flex-wrap gap-2 text-sm" role="group" aria-label="Filtres">
                    {(['all', ...used] as const).map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            aria-pressed={filter === c}
                            className={`pf-btn rounded-full border border-(--pf-ink)/25 px-4 py-2 transition-colors ${filter === c ? 'bg-(--pf-ink) text-(--pf-paper)' : ''}`}
                        >
                            {c === 'all' ? t(ui.all) : t(categories[c])}
                        </button>
                    ))}
                </div>
            </div>

            <ul className="group/list" onPointerLeave={() => setActive(null)}>
                {shown.map((p, i) => (
                    <li key={p.slug} className="border-b border-(--pf-ink)/20 transition-opacity duration-300 lg:group-hover/list:opacity-30 lg:hover:opacity-100!">
                        <Fade delay={i * 0.07}>
                        <Link
                            href={`/portfolio/${p.slug}?lang=${lang}`}
                            onPointerEnter={(e) => {
                                if (!active) {
                                    // pas de glissement depuis l'ancienne position
                                    x.jump(e.clientX);
                                    y.jump(e.clientY);
                                }
                                setActive(p.slug);
                                setPreview(p.slug);
                            }}
                            onFocus={() => {
                                setActive(p.slug);
                                setPreview(p.slug);
                            }}
                            className="group relative grid items-baseline gap-x-6 gap-y-1 py-6 focus-visible:outline-offset-4 lg:grid-cols-[1fr_16rem_10rem_5rem] lg:py-8"
                        >
                            <span className="inline-flex items-center gap-[0.2em] font-(family-name:--pf-serif) text-4xl leading-none transition-[translate,color] duration-500 ease-(--pf-ease) group-hover:translate-x-4 group-hover:text-(--pf-wine) md:text-7xl xl:text-8xl">
                                {p.title}
                                <Arrow dir="up-right" className="h-[0.35em] w-[0.35em] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                            </span>
                            <span className="text-sm text-(--pf-ink)/70">{p.client}</span>
                            <span className="text-sm text-(--pf-ink)/70">{t(categories[p.category])}</span>
                            <span className="hidden text-right text-sm tabular-nums lg:block">{p.year}</span>
                            <div className="relative mt-4 aspect-4/5 max-w-sm overflow-hidden lg:hidden">
                                <Image src={p.cover.src} alt={p.cover.alt} fill sizes="24rem" className="object-cover" />
                            </div>
                            {/* Trait rouge vin qui file sous la ligne survolée */}
                            <span aria-hidden className="pointer-events-none absolute inset-x-0 -bottom-px h-px origin-left scale-x-0 bg-(--pf-wine) transition-transform duration-700 ease-(--pf-ease) group-hover:scale-x-100" />
                        </Link>
                        </Fade>
                    </li>
                ))}
            </ul>

            {/* Aperçu flottant (desktop) : part de l'effet du projet, puis la vraie image s'ouvre depuis le centre */}
            <motion.div
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-40 hidden h-[26rem] w-[21rem] overflow-hidden lg:block"
                style={{ x, y, translateX: '12%', translateY: '-50%' }}
                initial={false}
                animate={active ? { opacity: 1, scale: 1, rotate: 0 } : { opacity: 0, scale: 0.7, rotate: -6 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
                <Image src={current.cover.src} alt="" fill sizes="21rem" className="object-cover" />
                {!reduce && <ShaderImage src={current.cover.src} effect={effects[current.category]} mode="ink" active={!!active} />}
            </motion.div>
        </section>
    );
}
